import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'

// ─── Configuration ──────────────────────────────────────────────
// Update this with your actual App Store URL once published
const APP_STORE_URL = 'https://apps.apple.com/app/miac/id6758415665'

const DISMISS_STORAGE_KEY = 'miac-smart-banner-dismissed-at'
const DISMISS_DURATION_MS = 14 * 24 * 60 * 60 * 1000 // Re-show after 14 days

// ─── Detection helpers ──────────────────────────────────────────

/** True when the page is loaded on an iOS device in a regular browser (not native app, not PWA). */
function shouldShowBanner(): boolean {
  if (typeof window === 'undefined') return false

  const ua = navigator.userAgent

  // Detect iOS (iPhone/iPad/iPod) — also covers iPad with desktop UA
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua))

  if (!isIOS) return false

  // Don't show inside the native Capacitor shell
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).Capacitor?.isNativePlatform?.()) return false

  // Don't show if running as an installed PWA / home-screen app
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window.navigator as any).standalone === true) return false

  // Safari already shows Apple's native Smart App Banner via the
  // <meta name="apple-itunes-app"> tag in index.html — skip our
  // custom banner there to avoid showing two banners.
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua)
  if (isSafari) return false

  // Check if user dismissed recently
  try {
    const dismissedAt = localStorage.getItem(DISMISS_STORAGE_KEY)
    if (dismissedAt) {
      const elapsed = Date.now() - parseInt(dismissedAt, 10)
      if (elapsed < DISMISS_DURATION_MS) return false
    }
  } catch {
    // localStorage unavailable — show banner anyway
  }

  return true
}

// ─── Component ──────────────────────────────────────────────────

export function SmartAppBanner() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    setVisible(shouldShowBanner())
  }, [])

  function dismiss() {
    try {
      localStorage.setItem(DISMISS_STORAGE_KEY, Date.now().toString())
    } catch {
      // ignore
    }
    setVisible(false)
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="overflow-hidden"
        >
          <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-800/80">
            {/* Close */}
            <button
              onClick={dismiss}
              className="flex shrink-0 items-center justify-center rounded-full p-0.5 text-slate-400 transition-colors hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
              aria-label="Dismiss app banner"
            >
              <X className="h-4 w-4" />
            </button>

            {/* App icon */}
            <img
              src="/app-logo.png"
              alt="Miac"
              className="h-10 w-10 shrink-0 rounded-xl shadow-sm"
            />

            {/* Text */}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                Miac — Prove Your Habits
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                FREE — On the App Store
              </p>
            </div>

            {/* CTA */}
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 rounded-full bg-blue-500 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white shadow-sm transition-colors hover:bg-blue-600 active:bg-blue-700"
            >
              Get
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
